/**
 * IncidentEditView - Editable full-screen view for existing incidents
 * Follows RecordDefectView pattern - KEY: All sections are EDITABLE
 * User can come back and add investigation details, corrective actions, etc.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  AlertTriangle,
  Calendar,
  MapPin,
  User,
  FileText,
  Search,
  CheckCircle,
  Clock,
  Plus,
  Trash2,
  Save,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Shield
} from 'lucide-react';
import { useCrew } from '../../../../hooks/useCrew';
import { useCustomDialogs } from '../../../../hooks/useCustomDialogs';
import { usePermissions } from '../../../../hooks/usePermissions';
// FT-356b: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header BackButton removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
import Dropdown from '../../../common/Dropdown';
import { Lock } from 'lucide-react';

const IncidentEditView = ({ incident, onBack, onSave, onQuickClose }) => {
  const { crew } = useCrew();
  const { showAlert, showConfirm, AlertDialog, ConfirmDialog } = useCustomDialogs();

  // FT-356b: register parent's onBack as the shell-level back handler.
  useSetBackHandler(onBack);

  // FT-06d P3: Role-based close permission
  const { currentUser, isSkipper, isFleet, isSuperAdmin } = usePermissions();
  const canCloseIncident = isSkipper || isFleet || isSuperAdmin;

  // Section expansion state
  const [investigationExpanded, setInvestigationExpanded] = useState(true);
  const [actionsExpanded, setActionsExpanded] = useState(true);

  // Form data - mirrors incident record structure
  const [formData, setFormData] = useState({
    // Basic Info (from initial report)
    type: '',
    date: '',
    time: '',
    location: '',
    description: '',
    immediateActions: '',
    severity: 'medium',
    reportedBy: '',

    // Investigation fields (EDITABLE)
    investigationStatus: 'pending',
    investigator: '',
    rootCause: '',
    contributingFactors: [],
    lessonsLearned: '',

    // Corrective actions (EDITABLE - array)
    remedialActions: [],
    preventiveMeasures: '',

    // Close out
    effectivenessVerified: false
  });

  const [newAction, setNewAction] = useState({
    description: '',
    assignedTo: '',
    dueDate: ''
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Crew options for dropdowns
  const crewOptions = useMemo(() => {
    if (!crew) return [];
    return crew.map(member => ({
      value: member.id || member.full_name || member.name,
      label: member.full_name || member.name || `${member.firstName || ''} ${member.lastName || ''}`.trim()
    }));
  }, [crew]);

  // Initialize form from incident
  useEffect(() => {
    if (incident) {
      setFormData({
        type: incident.type || 'safety',
        date: incident.date ? incident.date.split('T')[0] : '',
        time: incident.time || '',
        location: incident.location || '',
        description: incident.description || '',
        immediateActions: incident.immediateActions || '',
        severity: incident.severity || 'medium',
        reportedBy: incident.reportedBy || '',
        investigationStatus: incident.investigationStatus || 'pending',
        investigator: incident.investigator || '',
        rootCause: incident.rootCause || '',
        contributingFactors: incident.contributingFactors || [],
        lessonsLearned: incident.lessonsLearned || '',
        remedialActions: incident.remedialActions || [],
        preventiveMeasures: incident.preventiveMeasures || '',
        effectivenessVerified: incident.effectivenessVerified || false
      });
      setHasChanges(false);
    }
  }, [incident]);

  // Handle field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Handle array field changes (contributing factors)
  const handleToggleFactor = (factor) => {
    setFormData(prev => {
      const factors = prev.contributingFactors || [];
      const newFactors = factors.includes(factor)
        ? factors.filter(f => f !== factor)
        : [...factors, factor];
      return { ...prev, contributingFactors: newFactors };
    });
    setHasChanges(true);
  };

  // Add corrective action
  const handleAddAction = () => {
    if (!newAction.description.trim()) {
      showAlert('Missing Information', 'Please enter an action description', 'warning');
      return;
    }

    const action = {
      id: `action_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      description: newAction.description,
      assignedTo: newAction.assignedTo || '',
      dueDate: newAction.dueDate || null,
      status: 'open',
      completed: false,
      createdAt: new Date().toISOString()
    };

    setFormData(prev => ({
      ...prev,
      remedialActions: [...(prev.remedialActions || []), action]
    }));

    setNewAction({ description: '', assignedTo: '', dueDate: '' });
    setHasChanges(true);
  };

  // Toggle action completion
  const handleToggleActionComplete = (actionId) => {
    setFormData(prev => ({
      ...prev,
      remedialActions: prev.remedialActions.map(a =>
        a.id === actionId
          ? { ...a, completed: !a.completed, completedAt: !a.completed ? new Date().toISOString() : null }
          : a
      )
    }));
    setHasChanges(true);
  };

  // Remove action
  const handleRemoveAction = (actionId) => {
    setFormData(prev => ({
      ...prev,
      remedialActions: prev.remedialActions.filter(a => a.id !== actionId)
    }));
    setHasChanges(true);
  };

  // Save changes
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Determine status based on investigation progress
      let status = incident.status;
      if (formData.investigationStatus === 'in_progress' && status === 'open') {
        status = 'investigating';
      }
      if (formData.remedialActions?.length > 0 && formData.remedialActions.some(a => !a.completed)) {
        status = 'actions_pending';
      }

      await onSave({
        ...incident,
        ...formData,
        status,
        updatedAt: new Date().toISOString()
      });

      setHasChanges(false);
      showAlert('Saved', 'Incident updated successfully', 'success');
    } catch (error) {
      showAlert('Error', 'Failed to save changes', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Quick close handler
  const handleQuickClose = async () => {
    if (!formData.rootCause?.trim()) {
      showAlert('Missing Information', 'Please enter a root cause before closing', 'warning');
      return;
    }

    const confirmed = await showConfirm(
      'Quick Close Incident',
      'This will close the incident without requiring all corrective actions to be completed. Are you sure?'
    );

    if (confirmed) {
      await onQuickClose(incident.id, {
        rootCause: formData.rootCause,
        resolution: formData.lessonsLearned
      });
      onBack();
    }
  };

  // Full close handler
  const handleFullClose = async () => {
    const openActions = formData.remedialActions?.filter(a => !a.completed) || [];
    if (openActions.length > 0) {
      showAlert('Open Actions', `Please complete all ${openActions.length} corrective action(s) before closing`, 'warning');
      return;
    }

    if (!formData.rootCause?.trim()) {
      showAlert('Missing Information', 'Please enter a root cause before closing', 'warning');
      return;
    }

    const confirmed = await showConfirm(
      'Close Incident',
      'This will close the incident. All corrective actions are complete. Continue?'
    );

    if (confirmed) {
      const now = new Date().toISOString();
      const userName = currentUser?.name || 'Unknown User';
      await onSave({
        ...incident,
        ...formData,
        status: 'closed',
        // FT-06d P3: Write closure audit fields
        // FT-06e: Include closedByRole for "Name (Role)" display
        closedAt: now,
        closedBy: currentUser?.id || userName,
        closedByName: userName,
        closedByRole: currentUser?.role || '',
        investigationStatus: 'completed',
        updatedAt: now
      });
      onBack();
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'critical': 'bg-red-100 text-red-700',
      'high': 'bg-orange-100 text-orange-700',
      'medium': 'bg-yellow-100 text-yellow-700',
      'low': 'bg-blue-100 text-blue-700'
    };
    return colors[severity?.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  const getTypeLabel = (type) => {
    const types = {
      'accident': 'Accident',
      'near_miss': 'Near Miss',
      'hazard': 'Hazard',
      'environmental': 'Environmental',
      'equipment_failure': 'Equipment Failure',
      'personnel_injury': 'Personnel Injury',
      'safety': 'Safety'
    };
    return types[type] || type || 'Unknown';
  };

  const contributingFactorOptions = [
    'Human error',
    'Equipment failure',
    'Weather conditions',
    'Lack of training',
    'Inadequate procedures',
    'Communication failure',
    'Time pressure',
    'Fatigue',
    'Distraction',
    'Inadequate supervision'
  ];

  // Check if high severity (may need MAIB reporting)
  const isHighSeverity = ['critical', 'high'].includes(formData.severity?.toLowerCase());

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* FT-356b: header BackButton removed — bottom bar owns Back. */}
            <div className="flex items-center gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                    Incident Details
                  </h1>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getSeverityColor(formData.severity)}`}>
                    {formData.severity?.charAt(0).toUpperCase() + formData.severity?.slice(1)}
                  </span>
                </div>
                <p className="text-sm text-gray-500">
                  {incident?.id} - {getTypeLabel(formData.type)}
                </p>
              </div>
            </div>

            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* MAIB Warning for high severity */}
      {isHighSeverity && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-4">
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-800">MAIB Reporting May Be Required</p>
              <p className="text-amber-700 mt-1">
                This is a {formData.severity} severity incident. Serious casualties must be reported to MAIB within 24 hours.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* Section 1: What Happened (Basic Info) */}
        <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          <div className="bg-slate-700 px-5 py-3">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              What Happened
            </h2>
          </div>

          <div className="px-6 py-5 space-y-4">
            {/* Date, Time, Location Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="inline h-4 w-4 mr-1 text-cyan-600" />
                  Date
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="inline h-4 w-4 mr-1 text-cyan-600" />
                  Time
                </label>
                <input
                  type="time"
                  value={formData.time}
                  onChange={(e) => handleChange('time', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin className="inline h-4 w-4 mr-1 text-cyan-600" />
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="e.g., Engine room, Bridge"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>

            {/* Severity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <AlertCircle className="inline h-4 w-4 mr-1 text-cyan-600" />
                Severity
              </label>
              <Dropdown
                value={formData.severity}
                onChange={(v) => handleChange('severity', v)}
                width="w-full sm:w-48"
                options={[
                  { value: 'critical', label: 'Critical - Major incident' },
                  { value: 'high', label: 'High - Serious incident' },
                  { value: 'medium', label: 'Medium - Moderate incident' },
                  { value: 'low', label: 'Low - Minor incident' }
                ]}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FileText className="inline h-4 w-4 mr-1 text-cyan-600" />
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={4}
                placeholder="Describe what happened in detail..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            {/* Immediate Actions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Immediate Actions Taken
              </label>
              <textarea
                value={formData.immediateActions}
                onChange={(e) => handleChange('immediateActions', e.target.value)}
                rows={2}
                placeholder="What was done immediately after the incident?"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500"
              />
            </div>
          </div>
        </div>

        {/* Section 2: Investigation (Expandable, EDITABLE) */}
        <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          <button
            onClick={() => setInvestigationExpanded(!investigationExpanded)}
            className="w-full bg-slate-700 px-5 py-3 flex items-center justify-between text-left"
          >
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <Search className="h-5 w-5" />
              Investigation
              {formData.investigationStatus === 'completed' && (
                <span className="ml-2 px-2 py-0.5 bg-green-500 text-white text-xs rounded">Complete</span>
              )}
            </h2>
            {investigationExpanded ? (
              <ChevronUp className="h-5 w-5 text-white" />
            ) : (
              <ChevronDown className="h-5 w-5 text-white" />
            )}
          </button>

          {investigationExpanded && (
            <div className="px-6 py-5 space-y-4">
              {/* Investigation Status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Investigation Status
                  </label>
                  <Dropdown
                    value={formData.investigationStatus}
                    onChange={(v) => handleChange('investigationStatus', v)}
                    width="w-full"
                    options={[
                      { value: 'pending', label: 'Pending - Not started' },
                      { value: 'in_progress', label: 'In Progress' },
                      { value: 'completed', label: 'Completed' }
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <User className="inline h-4 w-4 mr-1 text-cyan-600" />
                    Investigator
                  </label>
                  <Dropdown
                    value={formData.investigator}
                    onChange={(v) => handleChange('investigator', v)}
                    width="w-full"
                    placeholder="Select investigator"
                    options={[
                      { value: '', label: 'Not assigned' },
                      ...crewOptions
                    ]}
                  />
                </div>
              </div>

              {/* Root Cause - KEY EDITABLE FIELD */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Root Cause <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.rootCause}
                  onChange={(e) => handleChange('rootCause', e.target.value)}
                  rows={3}
                  placeholder="What was the underlying cause of this incident?"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500"
                />
                <p className="mt-1 text-xs text-gray-500">Required before closing the incident</p>
              </div>

              {/* Contributing Factors */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contributing Factors
                </label>
                <div className="flex flex-wrap gap-2">
                  {contributingFactorOptions.map(factor => (
                    <button
                      key={factor}
                      type="button"
                      onClick={() => handleToggleFactor(factor)}
                      className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                        formData.contributingFactors?.includes(factor)
                          ? 'bg-cyan-100 border-cyan-500 text-cyan-700'
                          : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      {factor}
                    </button>
                  ))}
                </div>
              </div>

              {/* Lessons Learned */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lessons Learned
                </label>
                <textarea
                  value={formData.lessonsLearned}
                  onChange={(e) => handleChange('lessonsLearned', e.target.value)}
                  rows={2}
                  placeholder="What can be learned from this incident?"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Section 3: Corrective Actions (Expandable, EDITABLE) */}
        <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          <button
            onClick={() => setActionsExpanded(!actionsExpanded)}
            className="w-full bg-slate-700 px-5 py-3 flex items-center justify-between text-left"
          >
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Corrective Actions
              {formData.remedialActions?.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-white/20 text-white text-xs rounded">
                  {formData.remedialActions.filter(a => a.completed).length}/{formData.remedialActions.length}
                </span>
              )}
            </h2>
            {actionsExpanded ? (
              <ChevronUp className="h-5 w-5 text-white" />
            ) : (
              <ChevronDown className="h-5 w-5 text-white" />
            )}
          </button>

          {actionsExpanded && (
            <div className="px-6 py-5 space-y-4">
              {/* Existing Actions List */}
              {formData.remedialActions?.length > 0 && (
                <div className="space-y-3">
                  {formData.remedialActions.map((action) => (
                    <div
                      key={action.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${
                        action.completed ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                      }`}
                    >
                      <button
                        onClick={() => handleToggleActionComplete(action.id)}
                        className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          action.completed
                            ? 'bg-green-500 border-green-500 text-white'
                            : 'border-gray-300 hover:border-cyan-500'
                        }`}
                      >
                        {action.completed && <CheckCircle className="h-3 w-3" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${action.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                          {action.description}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-500">
                          {action.assignedTo && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {action.assignedTo}
                            </span>
                          )}
                          {action.dueDate && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Due: {new Date(action.dueDate).toLocaleDateString('en-GB')}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveAction(action.id)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Action */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Add Corrective Action</h4>
                <div className="space-y-3">
                  <textarea
                    value={newAction.description}
                    onChange={(e) => setNewAction(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    placeholder="Describe the corrective action..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Dropdown
                      value={newAction.assignedTo}
                      onChange={(v) => setNewAction(prev => ({ ...prev, assignedTo: v }))}
                      width="w-full"
                      placeholder="Assign to..."
                      options={[
                        { value: '', label: 'Unassigned' },
                        ...crewOptions
                      ]}
                    />
                    <input
                      type="date"
                      value={newAction.dueDate}
                      onChange={(e) => setNewAction(prev => ({ ...prev, dueDate: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500"
                      placeholder="Due date (optional)"
                    />
                  </div>
                  <button
                    onClick={handleAddAction}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-md hover:bg-cyan-100"
                  >
                    <Plus className="h-4 w-4" />
                    Add Action
                  </button>
                </div>
              </div>

              {/* Preventive Measures */}
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preventive Measures
                </label>
                <textarea
                  value={formData.preventiveMeasures}
                  onChange={(e) => handleChange('preventiveMeasures', e.target.value)}
                  rows={2}
                  placeholder="What measures will prevent this from happening again?"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Section 4: Close Out */}
        <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          <div className="bg-slate-700 px-5 py-3">
            <h2 className="text-base font-semibold text-white">Close Out</h2>
          </div>

          <div className="px-6 py-5 space-y-4">
            {/* Effectiveness Verification */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.effectivenessVerified}
                onChange={(e) => handleChange('effectivenessVerified', e.target.checked)}
                className="mt-1 h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Effectiveness Verified</span>
                <p className="text-sm text-gray-500">
                  Confirm that corrective actions have been effective in preventing recurrence (ISM Code 9.2)
                </p>
              </div>
            </label>

            {/* FT-06d P3: Close Buttons - Role-gated to skipper/fleet/super_admin */}
            {canCloseIncident ? (
              <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleQuickClose}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100"
                >
                  Quick Close (Minor Incident)
                </button>
                <button
                  onClick={handleFullClose}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4" />
                  Close Incident
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 pt-4 border-t border-gray-200 text-gray-500">
                <Lock className="h-4 w-4" />
                <span className="text-sm">Only Master or Fleet Manager can close incidents</span>
              </div>
            )}

            {/* Status info */}
            {canCloseIncident && formData.remedialActions?.some(a => !a.completed) && (
              <p className="text-sm text-amber-600">
                Note: {formData.remedialActions.filter(a => !a.completed).length} corrective action(s) still open.
                Complete them or use Quick Close for minor incidents.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <AlertDialog />
      <ConfirmDialog />
    </div>
  );
};

export default IncidentEditView;
